import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { NgbModal, NgbModalOptions} from '@ng-bootstrap/ng-bootstrap';
import { SCROLL_TO_OFFERS_QUERYPARAM } from 'src/app/constants/app.constants';
import { EligiblePet, EligiblePetsWrapper, PetSpeciesFilterableType, UnspecifiedBreeds } from 'src/app/interfaces/pet';
import { PetplanTrialApplication } from 'src/app/interfaces/petplan';
import { User } from 'src/app/interfaces/user';
import { LogService } from 'src/app/services/common/log.service';
import { RegistrationProgressData } from 'src/app/services/registration-progress/registration-progress.service';
import { RegistrationFlowService } from 'src/app/services/registration-flow/registration-flow.service';
import { RegistrationFlowStep } from 'src/app/constants/registration-flow.constants';
import { PetplanDeclineInsuranceModalComponent } from 'src/app/modals/petplan-decline-insurance-modal/petplan-decline-insurance-modal.component';
import { PetplanService } from 'src/app/services/petplan/petplan.service';
import { errorMessagesMap } from 'src/app/constants/error-messages';
import { ADD_PET_ID_STORAGE_KEY, IDENTIBASE_SUBSCRIBE_APPRECIATION_ROUTE } from 'src/app/constants/route.constants';
import { isPlatformBrowser } from '@angular/common';
import { ConfigService } from 'src/app/services/config/config.service';
import { PetplanSelectBreedModalComponent } from 'src/app/modals/petplan-select-breed-modal/petplan-select-breed-modal.component';
import { PetService } from 'src/app/services/pet/pet.service';
import { FeatureFlagsService } from 'src/app/services/feature-flag/feature-flag.service';

@Component({
  selector: 'app-petplan-welcome-gift',
  templateUrl: './petplan-welcome-gift.component.html',
  styleUrls: ['./petplan-welcome-gift.component.scss'],
})

export class PetplanWelcomePackComponent {
  public alert: { type: string; message: SafeHtml } | null = null;
  public loading = false;
  public user: User;
  public eligiblePetsWrapper: EligiblePetsWrapper;
  public petNames: string;
  public petSpecie: string;
  public bannerImagePath: string;
  public eligiblePet: EligiblePet;
  public brandName: string;
  public isDeclineChecked = false;
  public breedNamesList: string[] = [];
  private isBrowser;
  private companyPhoneNumber: string;
  private contactEmailAddress: string;
  private breedsList: Record<string, number>;
  private selectedBreedCode: number = null;
  private isSpaniel: boolean;
  private needsBreedSelection: boolean;
  useRebrandedLayout: boolean;
  // Shown while the welcome gift is a step still to complete in the user's flow
  public showProgressStepper: boolean;
  public RegistrationFlowStep = RegistrationFlowStep;

  constructor(
    private router: Router,
    private logService: LogService,
    private activatedRoute: ActivatedRoute,
    private petplanService: PetplanService,
    private _modalService: NgbModal,
    private sanitizer: DomSanitizer,
    private configService: ConfigService,
    private featureFlagService: FeatureFlagsService,
    private petService: PetService,
    private registrationFlowService: RegistrationFlowService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.user = this.activatedRoute.snapshot.data.user as User;
    this.eligiblePetsWrapper = this.activatedRoute.snapshot.data.eligiblePets as EligiblePetsWrapper;
    this.eligiblePet = this.getEligiblePetToOffer();
    this.companyPhoneNumber = this.configService.getTextFromKey('company-phone-number');
    this.contactEmailAddress = this.configService.getTextFromKey('contact-email-address');
    this.brandName = this.configService.get('displayName');
    this.useRebrandedLayout = this.featureFlagService.isEnabled('useRebrandedLayout');
    this.showProgressStepper = !this.registrationFlowService.isStepCompleted(RegistrationFlowStep.WelcomeGift);
    this.needsBreedSelection = Object.values(UnspecifiedBreeds).includes(this.eligiblePet.breedVenomCode);

    this.bannerImagePath = this.eligiblePet.speciesFilter === PetSpeciesFilterableType.Dog
    ? "/assets/images/partner/petplan/banner/banner-dog.png"
    : this.eligiblePet.speciesFilter === PetSpeciesFilterableType.Cat
    ? "/assets/images/partner/petplan/banner/banner-cat.png"
    : "/assets/images/partner/petplan/banner/banner-rabbit.png"

    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.registrationFlowService.loadSteps().subscribe();
    this.petNames = this.createTitle();
    if (this.needsBreedSelection) {
      this.getBreedsList();
    }
    if (this.eligiblePet.speciesFilter === PetSpeciesFilterableType.Dog) {
      this.petSpecie = "Dog";
    } else if (this.eligiblePet.speciesFilter === PetSpeciesFilterableType.Cat) {
      this.petSpecie = "Cat";
    } else {
      this.petSpecie = "Rabbit";
    }
  }

  // The breed modal is fed from the breed list, so the CTA waits for it when a breed has to be picked.
  public get isBreedListPending(): boolean {
    return this.needsBreedSelection && !this.breedNamesList.length;
  }

  private createTitle(): string {
    if (!!this.eligiblePet && this.eligiblePetsWrapper.eligiblePets.length > 0) {
      return `${this.createPetsListString(this.eligiblePetsWrapper)}.`;
    }

    // If there are no eligible pets, complete the step and redirect to dashboard.
    this.alert = {
      type: 'info',
      message:
        'You do not have any eligible pets for petplan. You\'ll get redirected in a few seconds!',
    };
    setTimeout(() => {
      this.forgetAddedPet();
      this.registrationFlowService
        .completeStep(RegistrationFlowStep.WelcomeGift)
        .subscribe(() =>
          this.router.navigate([this.getNextStepRoute()], { queryParams: { followUs: 'open' } })
        );
    }, 1000);
    return;
  }

  // The step after welcome-gift in the user's flow, normally the dashboard. Falls back to /dashboard.
  private getNextStepRoute(): string {
    return this.registrationFlowService.getNextRoute(RegistrationFlowStep.WelcomeGift) ?? '/dashboard';
  }

  private createPetsListString(pets): string {
    if (!pets.length) {
      return '';
    } else if (pets.length === 1) {
      return pets[0].name;
    } else if (pets.length === 2) {
      return `${pets[0].name} & ${pets[1].name}`;
    } else if (pets.length === 3) {
      return `${pets[0].name}, ${pets[1].name} & ${pets[2].name}`;
    } else {
      return `${pets[0].name}, ${pets[1].name} & your other pets`;
    }
  }

  public onDeclineChange(checked: boolean, checkbox: HTMLInputElement) {
    if (!checked) {
      return;
    }

    // The modal hands focus back to whatever had it on open, so blur first or "Insure" leaves the checkbox focused.
    checkbox.blur();

    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
    };

    const modalRef = this._modalService.open(PetplanDeclineInsuranceModalComponent, modalOptions);

    modalRef.componentInstance.eligiblePet = this.eligiblePet
    modalRef.result.then(
      (insure: boolean) => {
        if (insure) {
          this.isDeclineChecked = false;
        } else {
          this.declineWelcomeGift();
        }
      },
      () => (this.isDeclineChecked = false),
    );
  }

  private openSelectBreedModal(): Promise<string> {
    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
    };

    const modalRef = this._modalService.open(PetplanSelectBreedModalComponent, modalOptions);
    modalRef.componentInstance.petName = this.eligiblePet.name;
    modalRef.componentInstance.breedNamesList = this.breedNamesList;
    modalRef.componentInstance.petSpecie = this.petSpecie;
    modalRef.componentInstance.isSpaniel = this.isSpaniel;

    return modalRef.result;
  }

  public activateFreeInsurance() {
    if (this.loading) {
      return;
    }

    if (this.needsBreedSelection && this.selectedBreedCode === null) {
      this.openSelectBreedModal().then(
        (breed) => {
          this.selectedBreedCode = this.breedsList[breed];
          this.sendPetplanApplication();
        },
        () => {},
      );
      return;
    }

    this.sendPetplanApplication();
  }

  private declineWelcomeGift() {
    if (this.loading) {
      return;
    }

    this.completeWelcomeGiftStep()
    .subscribe(
      () => {
        this.alert = {
          type: 'success',
          message:
            'Your account information was updated. You\'ll get redirected in a few seconds!',
        };
        setTimeout(() => {
          this.router.navigate([this.getNextStepRoute()], {
            queryParams: { scrollTo: SCROLL_TO_OFFERS_QUERYPARAM, followUs: 'open' },
          });
        }, 1000);
      },
      (err) => {
        // Only re-enable on error; on success the controls stay locked until the delayed redirect.
        this.loading = false;
        this.isDeclineChecked = false;
        this.alert = {
          type: 'danger',
          message: 'An error has occurred. Please try again later.',
        };
        this.logService.error(
          'An error has occurred during welcome gift state save. Please try again later. ',
          err
        );
      }
    );
  }

  public closeAlert() {
    this.alert = null;
  }

  // Completes the step on the backend, for both claiming and skipping the gift.
  private completeWelcomeGiftStep(): Observable<void> {
    this.closeAlert();
    this.loading = true;
    this.forgetAddedPet();
    return this.registrationFlowService.completeStep(RegistrationFlowStep.WelcomeGift);
  }

  // The pet just added in the add pet flow when it is eligible, otherwise the first eligible pet
  private getEligiblePetToOffer(): EligiblePet {
    const eligiblePets = this.eligiblePetsWrapper.eligiblePets;
    const addedPetId = isPlatformBrowser(this.platformId) ? localStorage.getItem(ADD_PET_ID_STORAGE_KEY) : null;
    return eligiblePets.find((pet) => String(pet.id) === addedPetId) ?? eligiblePets[0];
  }

  // The welcome gift is the last step of the add pet flow
  private forgetAddedPet(): void {
    if (this.isBrowser) {
      localStorage.removeItem(ADD_PET_ID_STORAGE_KEY);
    }
  }

  private sendPetplanApplication() {
    this.closeAlert();
    if (this.user.pets.length < 1){
      this.alert = {
        type: 'danger',
        message: 'You do not have any pets registered in our system. Please come back after you have registered your pet.',
      };
      return;
    }

    this.loading = true;
    // Consent to share details with Petplan is given by activating, per the page's consent text.
    const request: PetplanTrialApplication = {
      isDataUsageAllowed: true,
      venomCode: this.selectedBreedCode
    };

    // One click, three calls in order: save the pet to Petplan, register the trial, then complete the step.
    this.petplanService.petplanSave(this.eligiblePet.id).pipe(
      switchMap(() => this.petplanService.submitPetplanTrialApplication(request, this.eligiblePet.id)),
      switchMap(() => this.completeWelcomeGiftStep()),
    ).subscribe({
      next: () => {
        // loading stays on so the controls can't be used while the next page loads.
        if (this.isBrowser) {
          const arrivedFromEmail = localStorage.getItem("arrived_from_email");

          if (arrivedFromEmail) {
            this.router.navigate(
              ['/account/' + IDENTIBASE_SUBSCRIBE_APPRECIATION_ROUTE],
              { queryParams: { subscriptionType: 'petplan4weeksfree' } }
            );
          } else {
            this.router.navigate(
              [this.getNextStepRoute()],
              { queryParams: { followUs: 'open' } }
            );
          }
        }
      },
      error: (err) => {
        // Petplan may have rejected the picked breed, so ask for it again on the next attempt.
        this.selectedBreedCode = null;
        this.handleApiError(err);
      },
    });
  }

  public getProgressData(){
    const result: RegistrationProgressData = {
      isFullNameRegistrationCompleted: true,
      isPetChipNumberRegistrationCompleted: true,
      isMembershipSelectionCompleted: true,
      isWelcomePackSelectCompleted: false,
    };
    return result;
  }

  private handleApiError(err: any): void {
    console.error(err);
    this.loading = false;

    const rawError = err?.error;
    const errorList = Array.isArray(rawError) ? rawError : (rawError ? [rawError] : []);

    let unknownErrorShown = false;
    let messages: string[] = [];

    if (errorList.length === 0) {
      this.addUnknownError(messages);
      unknownErrorShown = true;
    }

    for (const error of errorList) {
      const key = error?.error_message || (typeof error === 'string' ? error : null);
      const rawMessage = errorMessagesMap[key];

      const msg = rawMessage
        ?.replace('{petId}', this.eligiblePet?.id?.toString() || '')
        .replace('{{companyPhoneNumber}}', this.companyPhoneNumber);

      if (msg) {
        messages.push(msg);
      } else if (!unknownErrorShown) {
        this.addUnknownError(messages);
        unknownErrorShown = true;
      }
    }

    this.alert = {
      type: 'danger',
      message: this.sanitizer.bypassSecurityTrustHtml(messages.join('<br>'))
    };
  }

  private addUnknownError(messages: string[]) {
    messages.push(`We're unable to process your request. Please reach out to our support team either by emailing ${this.contactEmailAddress} or call us on ${this.companyPhoneNumber}.`);
  }

  private getBreedsList(): void {
    // Loading breeds list
    this.breedNamesList = [];
    this.petService
      .getBreedsFor(this.eligiblePet.speciesVenomCode)
      .subscribe(
        (breedsList) => {
          this.breedsList = breedsList;
          const filteredBreedsList = Object.keys(breedsList).filter(name => !name.toLowerCase().includes('unspecified')).sort();
          const spanielBreeds = filteredBreedsList.filter(name => name.toLowerCase().includes('spaniel'));
          this.breedNamesList = filteredBreedsList;
          if (this.eligiblePet.breedVenomCode === UnspecifiedBreeds.Spaniel) {
            this.isSpaniel = true;
            this.breedNamesList = spanielBreeds;
          }
        },
        (err) => {
          this.alert = {
            type: 'danger',
            message:
              'An error has occurred while retrieving some information. Please try again later.',
          };
        }
      )
      .add(() => {});
  }
}
